import { useEffect, useState } from "react";
import Container from "@/components/Container";
import Pager, { usePager } from "@/components/Pager";
import { api } from "@/lib/api";
import { ADMIN } from "@/constants/testIds";
import { toast } from "sonner";
import { ChevronDown, ChevronUp, Home, Mail, Phone, MapPin, Camera, Copy } from "lucide-react";

const LEAD_TYPES = ["all","seller","investor","sourcing_partner","estate_agent","contractor","designer","surveyor","solicitor","enquiry"];
const STATUSES = ["new","contacted","qualified","follow_up","converted","not_suitable","archived"];

// Backend serves photos at /api/files/<path> — join with backend URL so admin can preview
const BACKEND = process.env.REACT_APP_BACKEND_URL || "";
const abs = (u) => {
  if (!u) return "";
  if (u.startsWith("http")) return u;
  if (u.startsWith("/api/files/")) return BACKEND + u;
  return u;
};

export default function LeadsAdmin() {
  const [items, setItems] = useState([]);
  const [type, setType] = useState("all");
  const [expanded, setExpanded] = useState({});

  const load = () => {
    const q = type === "all" ? "" : `?lead_type=${type}`;
    api.get(`/leads${q}`).then(r => setItems(r.data || []));
  };
  useEffect(() => { load(); }, [type]);

  const update = async (id, status) => { await api.put(`/leads/${id}`, { status }); load(); toast.success("Updated"); };
  const del = async (id) => { if (!window.confirm("Delete lead?")) return; await api.delete(`/leads/${id}`); load(); };
  const toggle = (id) => setExpanded((e) => ({ ...e, [id]: !e[id] }));
  const copyText = (t) => { navigator.clipboard.writeText(t).then(() => toast.success("Copied")); };

  const pager = usePager(items);

  return (
    <Container className="py-8 md:py-12">
      <p className="overline">CRM</p>
      <h1 className="mt-2 text-4xl md:text-5xl font-serif text-ivory">Leads</h1>

      <div className="mt-6 flex flex-wrap gap-2">
        {LEAD_TYPES.map(t => (
          <button key={t} onClick={() => setType(t)}
            className={`px-3 py-1.5 text-xs border transition-colors ${type===t ? "border-gold text-gold" : "border-charcoal-600 text-ivory/60 hover:border-ivory/40"}`}
            data-testid={`leads-filter-${t}`}
          >
            {t.replace(/_/g," ")}
          </button>
        ))}
      </div>

      {items.length === 0 && (
        <div className="mt-8 luxury-card p-10 text-center text-ivory/40 text-sm">No leads.</div>
      )}

      <div className="mt-6 space-y-3">
        {pager.pageItems.map(l => {
          const isOpen = !!expanded[l.id];
          const hasProperty =
            l.property_type || l.bedrooms || l.bathrooms || l.tenure || l.condition ||
            l.reason_for_sale || l.timeline || l.asking_price || l.budget || l.property_address;
          const imgs = Array.isArray(l.image_urls) ? l.image_urls : [];

          return (
            <div key={l.id} className="luxury-card overflow-hidden" data-testid={ADMIN.leadRow(l.id)}>
              {/* Row header */}
              <div className="p-4 md:p-5 flex flex-wrap md:flex-nowrap items-center gap-4">
                <div className="flex items-center gap-3 flex-1 min-w-0">
                  {imgs[0] ? (
                    <img src={abs(imgs[0])} alt="" className="w-14 h-14 object-cover border border-charcoal-600/60 shrink-0" />
                  ) : (
                    <div className="w-14 h-14 border border-charcoal-600/60 flex items-center justify-center text-ivory/30 shrink-0">
                      <Home size={18} strokeWidth={1.2} />
                    </div>
                  )}
                  <div className="min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <p className="text-ivory font-medium truncate">{l.name}</p>
                      <span className="text-[0.6rem] uppercase tracking-[0.15em] text-bronze border border-bronze/40 px-1.5 py-0.5">
                        {(l.lead_type || "").replace(/_/g," ")}
                      </span>
                      {imgs.length > 0 && (
                        <span className="text-[0.6rem] uppercase tracking-[0.15em] text-gold border border-gold/40 px-1.5 py-0.5 inline-flex items-center gap-1">
                          <Camera size={10}/> {imgs.length}
                        </span>
                      )}
                    </div>
                    <div className="mt-1 flex items-center gap-3 text-xs text-ivory/50 flex-wrap">
                      <a href={`mailto:${l.email}`} className="text-gold/80 hover:text-gold inline-flex items-center gap-1">
                        <Mail size={11}/> {l.email}
                      </a>
                      {l.phone && (
                        <a href={`tel:${l.phone}`} className="hover:text-ivory inline-flex items-center gap-1">
                          <Phone size={11}/> {l.phone}
                        </a>
                      )}
                      {l.property_address && (
                        <span className="inline-flex items-center gap-1 truncate max-w-[16rem]">
                          <MapPin size={11}/> {l.property_address}
                        </span>
                      )}
                    </div>
                  </div>
                </div>

                <div className="flex items-center gap-2 shrink-0">
                  <select
                    value={l.status}
                    onChange={e => update(l.id, e.target.value)}
                    className="bg-charcoal-800 border border-charcoal-600 px-2 py-1.5 text-xs text-ivory"
                    data-testid={`lead-status-${l.id}`}
                  >
                    {STATUSES.map(s => <option key={s} className="bg-charcoal-800">{s}</option>)}
                  </select>
                  <button
                    onClick={() => toggle(l.id)}
                    className="btn-outline !py-1.5 !px-3 !text-[0.65rem]"
                    data-testid={`lead-toggle-${l.id}`}
                  >
                    {isOpen ? <><ChevronUp size={12}/> Hide</> : <><ChevronDown size={12}/> Details</>}
                  </button>
                  <button
                    onClick={() => del(l.id)}
                    className="btn-outline !py-1.5 !px-2 !text-[0.65rem] !border-ruby/40 !text-ruby-light"
                    data-testid={`lead-delete-${l.id}`}
                  >
                    Delete
                  </button>
                </div>
              </div>

              {/* Expanded detail */}
              {isOpen && (
                <div className="border-t border-charcoal-600/60 p-5 md:p-6 space-y-6" data-testid={`lead-detail-${l.id}`}>
                  {hasProperty && (
                    <div>
                      <p className="text-[0.65rem] uppercase tracking-[0.18em] text-gold mb-3">Property</p>
                      <div className="grid grid-cols-2 md:grid-cols-4 gap-x-6 gap-y-3 text-sm">
                        <Detail k="Type" v={l.property_type} />
                        <Detail k="Bedrooms" v={l.bedrooms} />
                        <Detail k="Bathrooms" v={l.bathrooms} />
                        <Detail k="Tenure" v={l.tenure} />
                        <Detail k="Condition" v={l.condition} />
                        <Detail k="Reason for sale" v={l.reason_for_sale} />
                        <Detail k="Timeline" v={l.timeline} />
                        <Detail k="Asking price" v={l.asking_price} />
                        <Detail k="Budget floor" v={l.budget} />
                        <Detail k="Address" v={l.property_address} wide />
                      </div>
                    </div>
                  )}

                  {imgs.length > 0 && (
                    <div>
                      <p className="text-[0.65rem] uppercase tracking-[0.18em] text-gold mb-3">Photos · {imgs.length}</p>
                      <div className="grid grid-cols-3 md:grid-cols-6 gap-2">
                        {imgs.map((u, i) => (
                          <a key={i} href={abs(u)} target="_blank" rel="noopener noreferrer"
                             className="aspect-[4/3] block bg-charcoal-800 border border-charcoal-600/60 overflow-hidden hover:border-gold transition-colors"
                             data-testid={`lead-photo-${l.id}-${i}`}>
                            <img src={abs(u)} alt="" className="w-full h-full object-cover" />
                          </a>
                        ))}
                      </div>
                    </div>
                  )}

                  {(l.property_details || l.message) && (
                    <div>
                      <div className="flex items-center justify-between mb-2">
                        <p className="text-[0.65rem] uppercase tracking-[0.18em] text-gold">Notes from seller</p>
                        <button onClick={() => copyText(l.property_details || l.message || "")}
                                className="text-[0.6rem] uppercase tracking-[0.15em] text-ivory/50 hover:text-gold inline-flex items-center gap-1">
                          <Copy size={10}/> Copy
                        </button>
                      </div>
                      <div className="bg-charcoal-800/50 border border-charcoal-600/60 p-4 text-sm text-ivory/80 whitespace-pre-line">
                        {l.property_details || l.message}
                      </div>
                    </div>
                  )}

                  <div className="flex items-center justify-between text-[0.65rem] uppercase tracking-[0.15em] text-ivory/40">
                    <span>Received {new Date(l.created_at).toLocaleString("en-GB", { day: "numeric", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" })}</span>
                    <span>via {l.source_page || "—"}</span>
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>
      <Pager {...pager} testid="leads-pager" />
    </Container>
  );
}

function Detail({ k, v, wide }) {
  if (!v) return null;
  return (
    <div className={wide ? "col-span-2 md:col-span-4" : ""}>
      <p className="text-[0.6rem] uppercase tracking-[0.15em] text-ivory/40">{k}</p>
      <p className="mt-1 text-ivory/90">{v}</p>
    </div>
  );
}
